/**
 * Choice Screen Component
 *
 * Initial decision screen shown when a quiz loads.
 * Users choose between exploring the map first or starting the quiz immediately.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Map, Play } from "lucide-react";

interface ChoiceScreenProps {
  quizTitle: string;
  onExplore: () => void;
  onStartQuiz: () => void;
}

export function ChoiceScreen({ quizTitle, onExplore, onStartQuiz }: ChoiceScreenProps) {
  return (
    <div className="absolute inset-0 z-modal flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
      <Card className="w-full max-w-2xl shadow-lg">
        <CardHeader className="text-center pb-4">
          <CardTitle className="text-3xl font-bold text-slate-900">
            {quizTitle}
          </CardTitle>
          <p className="text-slate-600 mt-2">
            Ready to test yourself, or explore the map first?
          </p>
        </CardHeader>

        <CardContent className="grid md:grid-cols-2 gap-4 pt-6">
          {/* Explore Option */}
          <Card
            className="border-2 border-blue-200 hover:border-blue-400 transition-colors cursor-pointer group"
            onClick={onExplore}
          >
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <Map className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Explore First</h3>
              <p className="text-sm text-slate-600 mb-4">
                Study the map with labels visible before starting the quiz
              </p>
              <Button variant="outline" className="w-full" onClick={onExplore}>
                📖 Explore Map
              </Button>
            </CardContent>
          </Card>

          {/* Start Quiz Option */}
          <Card
            className="border-2 border-green-200 hover:border-green-400 transition-colors cursor-pointer group"
            onClick={onStartQuiz}
          >
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200 transition-colors">
                <Play className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Start Quiz Now</h3>
              <p className="text-sm text-slate-600 mb-4">
                Jump right in and test your knowledge immediately
              </p>
              <Button variant="outline" className="w-full" onClick={onStartQuiz}>
                ▶️ Start Quiz
              </Button>
            </CardContent>
          </Card>
        </CardContent>

        <div className="px-6 pb-6 pt-2">
          <p className="text-xs text-center text-slate-500">
            💡 Tip: You can always restart the quiz to try different approaches
          </p>
        </div>
      </Card>
    </div>
  );
}
